import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  PROJECT_TEAM_ROLES,
  useUpdateProjectTeam,
  type ProjectTeam,
  type ProjectTeamRole,
} from '@/api/projects';

interface EditTeamRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** The project-team row being edited (null when closed). */
  team: ProjectTeam | null;
  /** Current user is a project owner — only owners may grant/keep the owner role. */
  actorIsOwner: boolean;
  /** The team being edited is currently the project's sole owner. */
  isLastOwner: boolean;
}

export function EditTeamRoleDialog({
  open,
  onOpenChange,
  projectId,
  team,
  actorIsOwner,
  isLastOwner,
}: EditTeamRoleDialogProps) {
  const { show } = useToast();
  const [role, setRole] = useState<ProjectTeamRole>('member');

  useEffect(() => {
    if (team) setRole(team.role);
  }, [team]);

  // Non-owners can't grant the owner role (privilege escalation); a non-owner
  // can't modify an owner team, and the last owner can't be demoted.
  const roleOptions = PROJECT_TEAM_ROLES.filter((r) => r.value !== 'owner' || actorIsOwner);
  const cannotModifyOwner = !actorIsOwner && team?.role === 'owner';
  const demotingLastOwner = isLastOwner && role !== 'owner';
  const blocked = cannotModifyOwner || demotingLastOwner;

  const update = useUpdateProjectTeam(projectId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Role updated' });
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not update role' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Change role</DialogTitle>
          <DialogDescription>
            Update the role for “{team?.team_name ?? 'this team'}” in this project.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Role" required>
            {() => (
              <Select value={role} onValueChange={(v) => setRole(v as ProjectTeamRole)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label} — {r.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {blocked && (
            <Alert tone="warning" style={{ marginTop: 'var(--vn-space-3)' }}>
              {cannotModifyOwner
                ? "Only a project owner can change an owner team's role."
                : 'A project must keep at least one owner team.'}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!team || update.isPending || blocked}
            onClick={() => team && !blocked && update.mutate({ teamId: team.team_id, role })}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
